import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Fetch the set of pubkeys the current user follows (kind 3 contact list).
 * Returns an empty set when logged out or when no contact list is found.
 */
export function useFollows(): { follows: Set<string>; isLoading: boolean } {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const query = useQuery<string[]>({
    queryKey: ['follows', user?.pubkey ?? ''],
    queryFn: async (c) => {
      if (!user?.pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const [event] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal },
      );
      if (!event) return [];
      return event.tags
        .filter((t) => t[0] === 'p' && t[1])
        .map((t) => t[1]);
    },
    enabled: !!user?.pubkey,
    staleTime: 5 * 60 * 1000,
  });

  const follows = useMemo(() => new Set(query.data ?? []), [query.data]);
  return { follows, isLoading: query.isLoading };
}
